/**
 * Copyright 2020 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import root from 'window-or-global';
import { type StackedPartCardContainerProps, type CertonaClickItem } from '../../interface';
import styles from './styles.module.scss';
import cx from 'classnames';
import azCommonStyles from '@/theme/globals.module.scss';
import { HorizontalPartCard } from '../HorizontalPartCard';
import { LargePartCard } from '../LargePartCard';
import { certonaPageType } from '@/constants/certonaPageType';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { useUtagReady } from '@/utils/analytics/useUtagReady';
import { useLabels } from '@/hooks/useLabels';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { routePaths } from '@/constants/routePaths';
import { useCertonaAddToCartFlag } from '@/hooks/useCertonaAddToCartCTAFlag';
import { getAddToCartInfo } from '../../utils/getAddToCartInfo';
import { sessionStorage } from '@/utils/sessionStorage';
import { useCertonaAddToCartVisibility } from '../../hooks/useCertonaAddToCartVisibility';
import { useHeaderData } from '@/features/header/api/getHeader';
import { Fit } from '@az/starc-ui-icons';
import { Icon } from '@az/starc-ui';
import { useProductSkuDeals } from '@/features/product/api/getProductDeals';
import { useFeatureFlag } from '@/features/globalConfig/hooks/useFeatureFlag';
import { useMemo, useState, useRef, useEffect } from 'react';

const labelMap = {
  lblDelivery: 'label_threshold_delivery',
  lblSpend: 'label_threshold_spend',
  lblThresholdContainerHeader: 'label_ThresholdContainerHeader',
  lblCTJFitsYour: 'label_fitment_badge_fits_your',
};

const StackedPartCardContainer = (props: StackedPartCardContainerProps) => {
  const {
    title,
    partsList,
    pricingInfo,
    contentsAligment = 'vertical',
    skuDetailsData,
    addToCartAnalyticsandSuccessMessage,
    onCertonaRecommendationClick,
    updateAfterAddtoCartClick,
    pageType,
    thresholdHeaderText,
    setAddedCertonaItemObject,
    inDrawer,
    certonaType,
    getCtjAnalytics,
    recTabNumber,
    attributionToken,
    certonaScheme,
    leadingItemFulfillmentId,
  } = props;

  const { isUtagReady: isUtagDefined } = useUtagReady();
  const { data: headerData } = useHeaderData();
  const preferredVehicleName = headerData?.vehicleMap?.vehicleName;

  // Check if at least one CTJ item fits the vehicle
  const ctjItemsFits = partsList.some(
    (part) => part.vehicleFitment === 'VEHICLE_SPECIFIC' && part.vehicleFit === 'FITS',
  );

  const { lblThresholdContainerHeader, lblCTJFitsYour } = useLabels(labelMap);
  const router = useLegacyRouter();
  const isCart = router.pathname === '/cart';
  const isRecommendationsLandingPage = router.asPath.includes(
    routePaths.landingPageRecommendations,
  );
  const isCertonaAddToCartCTAEnabled = useCertonaAddToCartFlag();

  const showAddToCartCTA =
    inDrawer || isCart || isRecommendationsLandingPage || isCertonaAddToCartCTAEnabled;
  const skuIds = partsList.filter((part) => !!part.partgroup_id).map((part) => part.id);
  const { shouldShowAddToCart, skuConfigs, isLoading } = useCertonaAddToCartVisibility(skuIds);
  const isDealsEnabled = useFeatureFlag('IS_DEALS_PRODUCT_RECS_ENABLED') === 'true';
  const [carouselNearViewport, setCarouselNearViewport] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setCarouselNearViewport(entry.isIntersecting);
      },
      { rootMargin: '250px' },
    );

    if (carouselRef.current) {
      observer.observe(carouselRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const dealSkuIds = useMemo(
    () => partsList?.filter((item) => item.isDeal)?.map((item) => item.id) ?? [],
    [partsList],
  );
  const { data: dealsData, isLoading: isDealsLoading } = useProductSkuDeals({
    skuIds: dealSkuIds,
    enabled: isDealsEnabled && dealSkuIds.length > 0 && carouselNearViewport,
  });
  const trackCertonaLinkInteractionBySection = (linkName: string, section: string, id: string) => {
    const utag = root.utag_data;
    const sectionName = `${section ?? utag?.pageType}_certona`;
    const data = {
      productLinkName: linkName,
      clickedProductId: id,
      pageName: root.utag_data?.pageName ?? '',
      pageType: root.utag_data?.pageType ?? '',
      siteSection: root.utag_data?.siteSection ?? '',
      eventType: sectionName.replace(/\s/g, '_').toLowerCase(),
    };
    clickTrack(data);
  };
  const handleCertonaClick = (linkName: string, id: string, item?: CertonaClickItem) => {
    onCertonaRecommendationClick?.();
    root.sessionStorage.setItem('productType', certonaType === 'CTJ' ? 'affinity' : 'certona');
    const token = item?.attributionToken || attributionToken;
    if (token) {
      sessionStorage.setItem('googleRetailRecommendationAttributionToken', token);
    }
    if (isUtagDefined === undefined && pageType !== certonaPageType.shoppingCartPage) {
      trackCertonaLinkInteractionBySection(linkName, pageType, id);
    }
  };

  return (
    <div
      ref={carouselRef}
      className={cx(
        contentsAligment === 'vertical'
          ? styles.verticalStackedBlock
          : styles.horizontalStackedBlock,
        styles.newProductDesign,
        { [styles.inDrawer]: inDrawer },
      )}
      data-testid="carousel"
    >
      {!!title && (
        <>
          <h2
            className={cx(styles.partTitle, azCommonStyles['az-padding-bottom-4xs'])}
            data-testid="dynamic-content"
          >
            {title}
          </h2>
          <div className={styles.separator} />
        </>
      )}

      {certonaType === 'CTJ' && preferredVehicleName && ctjItemsFits && (
        <div className={styles.ctjFitmentBadgingContainer}>
          <div className={styles.ctjFitmentBadge}>
            <div className={styles.ctjFitmentIconContainer}>
              <Icon svg={Fit} aria-hidden="true" className={styles.ctjFitmentIcon} />
            </div>
            <div className={styles.ctjFitmentText}>
              <span>{lblCTJFitsYour}</span>
              <span className={styles.ctJVehicleText}>{preferredVehicleName}</span>
            </div>
          </div>
        </div>
      )}

      {!!thresholdHeaderText && (
        <>
          <h2 className={styles.thresholdHeaderText} data-testid="dynamic-content">
            {`${lblThresholdContainerHeader}`}
          </h2>
        </>
      )}
      <ul className={styles.cardsList}>
        {partsList.map(
          ({
            id,
            product_image_url,
            product_url_relative,
            product_name,
            total_review_count,
            average_rating,
            rating,
            productId,
            attributionToken: itemAttributionToken,
            partgroup_id,
            isDeal,
            availableFulfillmentIds,
          }) => {
            // Get inventory config for this SKU from the new inventories API
            const inventoryConfig = skuConfigs[id];
            const partGroupId = partgroup_id;
            const addToCartInfo = getAddToCartInfo({
              skuDetailsData,
              id,
              productId,
              partGroups: partGroupId,
              inventoryConfig,
              availableFulfillmentIds,
              leadingItemFulfillmentId,
            });
            // CTJ items: show ATC based solely on the prop — isCompleteTheJob upstream
            // Certona recs: require partGroups (from Monetate) and inventory API check.
            const isAddToCartAreaEnabled = !!(props?.showAddToCartCTA ?? showAddToCartCTA);
            const skuShouldShowAddToCart =
              certonaType === 'CTJ' ? true : shouldShowAddToCart(id) && !!partGroupId;

            return (
              <li key={id} className={styles.cardItem}>
                {contentsAligment === 'vertical' ? (
                  <LargePartCard
                    key={id}
                    itemId={id}
                    part={{
                      isDeal,
                      properties: {
                        seoUrl: product_url_relative,
                        imageUrl: product_image_url,
                        makeModelYearPath: undefined,
                        refPageType: '',
                      },
                      label: product_name,
                      rating,
                      totalReviews: total_review_count,
                      averageRating: average_rating,
                    }}
                    pricingInfo={pricingInfo?.[id]}
                    addToCartData={addToCartInfo}
                    actionHandler={(linkName: string, itemId: string) =>
                      handleCertonaClick(linkName, itemId, {
                        attributionToken: itemAttributionToken,
                      })
                    }
                    addToCartAnalyticsandSuccessMessage={addToCartAnalyticsandSuccessMessage}
                    setAddedCertonaItemObject={setAddedCertonaItemObject}
                    showAddToCartCTA={isAddToCartAreaEnabled}
                    shouldShowAddToCart={skuShouldShowAddToCart}
                    isLoadingAddToCart={certonaType === 'CTJ' ? false : isLoading}
                    recTabNumber={recTabNumber}
                    certonaScheme={certonaScheme}
                    dealsData={dealsData}
                    isDealsLoading={isDealsLoading}
                    visibleDealSkuIds={dealSkuIds}
                  />
                ) : (
                  <HorizontalPartCard
                    key={id}
                    itemId={id}
                    part={{
                      properties: {
                        seoUrl: product_url_relative,
                        imageUrl: product_image_url,
                        makeModelYearPath: undefined,
                        refPageType: '',
                      },
                      isDeal,
                      label: product_name,
                      rating,
                      totalReviews: total_review_count,
                      averageRating: average_rating,
                    }}
                    pricingInfo={pricingInfo?.[id]}
                    addToCartData={addToCartInfo}
                    actionHandler={(linkName: string, itemId: string) =>
                      handleCertonaClick(linkName, itemId, {
                        attributionToken: itemAttributionToken,
                      })
                    }
                    addToCartAnalyticsandSuccessMessage={addToCartAnalyticsandSuccessMessage}
                    updateAfterAddtoCartClick={updateAfterAddtoCartClick}
                    setAddedCertonaItemObject={setAddedCertonaItemObject}
                    showAddToCartCTA={isAddToCartAreaEnabled}
                    shouldShowAddToCart={skuShouldShowAddToCart}
                    isLoadingAddToCart={certonaType === 'CTJ' ? false : isLoading}
                    inDrawer={inDrawer}
                    cardStyle={'horizontal_stacked'}
                    certonaType={certonaType}
                    getCtjAnalytics={getCtjAnalytics}
                    certonaScheme={certonaScheme}
                    dealsData={dealsData}
                    isDealsLoading={isDealsLoading}
                    visibleDealSkuIds={dealSkuIds}
                  />
                )}
              </li>
            );
          },
        )}
      </ul>
    </div>
  );
};

export default StackedPartCardContainer;
